<script lang="ts" setup>
import type { HousingCard } from '~/models/HousingCard';
import type { Search } from '~/models/Search';
import randomString from '~/composables/use-random-string';
import { getImageLoadingAttribute } from '~/utils/image-loading-attribute';

interface WatchingEvent {
  housingId: number;
  watched: null | string;
}

const { startWatchingOffer, stopWatchingOffer } = useWatchOffer();
const isAuthenticated = useAuthState();
const { showToast } = useToast();
const isDesktop = useIsDesktop();
const router = useRouter();
const { t } = useI18n();

const emit = defineEmits<{
  (event: 'mouseenter'): void;
  (event: 'mouseleave'): void;
  (event: 'report'): void;
  (event: 'update-watching', value: { housingId: number; watched: null | string }): void;
}>();

const props = withDefaults(
  defineProps<{
    searchCard: HousingCard;
    id?: string;
    cardIndex: number;
    showHidden?: boolean;
    showMore?: boolean;
    showLike?: boolean;
  }>(),
  {
    id: () => randomString(),
    showHidden: false,
    showMore: true,
    showLike: true,
  },
);

const housingData = ref(props.searchCard.housing);
const housingActions = computed(() => props.searchCard.actions);
const housingActionsWatched = computed(() => props.searchCard.actions?.watched);
const targetUserId = computed(() => props.searchCard.targetUserId);
const housing = computed(() => housingData.value);
const user = computed(() => props.searchCard.user);
const search = computed<Search>(() => props.searchCard.search);

const { class: hoverClass, events: hoverEvents } = useHover(emit, props);
const moreActionsType = ref('');
const conversionModalStatus = ref('close');

function checkIsLoggedIn(type: string, toggle: () => void, targetUserId: number | null = null, housingActionsWatched?: string | null | undefined | boolean) {
  toggle();
  if (!isAuthenticated.value) {
    showNotLoggedIn(type);
    return;
  }

  if (type === 'report') {
    emit('report');
  }

  if (type === 'toggle_like') {
    toggleLike(targetUserId, housingActionsWatched);
  }
}
function updateWatching(value: WatchingEvent) {
  emit('update-watching', value);
}

async function toggleLike(targetUserId: number | null, watched: string | null | undefined | boolean) {
  const isAdding = watched === null;
  const actionFunction = isAdding ? startWatchingOffer : stopWatchingOffer;
  try {
    const response = await actionFunction(targetUserId || 0);
    if (response) {
      updateWatching({
        housingId: props.searchCard.housing.id,
        watched: response.data.linkActions.watched || null,
      });
    } else {
      showToast('error', t('error'), t('ad-not-added-to-watch-list'));
    }
  } catch (error) {
    showToast('error', t('error'), t('unexpected-error-occurred'));
    console.log(error);
  }
}

function showNotLoggedIn(type: string) {
  moreActionsType.value = type;
  handleNoteRegisteredInfo('open');
}

const housingImages = computed(() => (housing.value.imageCollection.images.length > 0 ? housing.value.imageCollection.images : [housing.value.previewImage]));
function gotoHousing() {
  router.push(`/${housing.value.nuxtUriString}`);
}

function navigateToChat(userGuid: string) {
  if (!isAuthenticated.value) {
    showNotLoggedIn('contact');
    return;
  }
  const path = `/messaging/chat/${userGuid}`;
  router.push(path);
}

function handleNoteRegisteredInfo(action: 'open' | 'close') {
  conversionModalStatus.value = action;
}

const getLoadingAttribute = (idx: number) => {
  return getImageLoadingAttribute(idx, props.cardIndex, isDesktop.value);
};
</script>

<template>
  <article :class="{ 'is--mobile': !isDesktop }" class="tw-housing-card tw-box-variant--box">
    <NuxtLink :to="`/${housing.nuxtUriString}`" :title="`Angebot von ${user?.displayName}`" v-on="{ ...hoverEvents }">
      <div class="tw-housing-card--card">
        <div class="tw-housing-card--gallery">
          <tw-ui-carousel
            :class="[hoverClass]"
            :interval="false"
            :show-counter="housing.imageCollection.images.length > 0"
            :show-navigation="false"
            :show-next-button="housing.imageCollection.images.length > 1"
            :show-previous-button="housing.imageCollection.images.length > 1"
            :show-conversion-overlay="!isAuthenticated"
            @click.stop.prevent
          >
            <img
              v-for="(image, idx) in housingImages"
              :key="`TwHousingCard_${housing.id}_Carousel_img_${idx}`"
              :alt="t('img_alt', [idx + 1])"
              :src="image.urlPaths.small"
              @click="gotoHousing"
              @keydown.enter="gotoHousing"
              :loading="getLoadingAttribute(idx)"
            />
          </tw-ui-carousel>
        </div>

        <tw-housing-card-housing-info
          :housing="housing"
          :show-housing-properties="6"
          :user-guid="housing.userGuid"
          @contact-button-clicked="showNotLoggedIn('contact')"
        />
      </div>
      <tw-search-card-search-info :search="search" :user="user" :currency="housing.currency" :show-housing-properties="true" />
      <div v-if="!isDesktop" class="tw-housing-card--contact">
        <tw-ui-button @click.stop.prevent="navigateToChat(housing.userGuid)" class="tw-button action--contact tw-box-variant--box">
          {{ t('contact_button') }}
        </tw-ui-button>
      </div>
    </NuxtLink>
    <ul class="tw-housing-card--actions">
      <li v-if="props.showLike">
        <tw-housing-btn-like v-if="isAuthenticated && !housingActions.watched" @like="updateWatching($event)" :housing="housing" :target-user-id="targetUserId">
          <template #button="{ onClick, loading }">
            <button class="tw-housing-content-actions-btn-like" @click="onClick">
              <Icon name="bi:heart" :class="['heart-icon', { 'heart-icon-loading': loading }]" />
            </button>
          </template>
        </tw-housing-btn-like>
        <tw-housing-btn-unlike
          v-else-if="isAuthenticated && housingActions.watched"
          @unlike="updateWatching($event)"
          :housing="housing"
          :target-user-id="targetUserId"
        >
          <template #button="{ onClick, loading }">
            <button class="tw-housing-content-actions-btn-unlike" @click="onClick">
              <Icon name="bi:heart-fill" :class="['heart-icon', { 'heart-icon-loading': loading }]" />
            </button>
          </template>
        </tw-housing-btn-unlike>
        <button v-else class="tw-housing-content-actions-btn-like" @click="showNotLoggedIn('toggle_like')">
          <Icon name="bi:heart" :class="['heart-icon', { 'heart-icon-loading': false }]" />
        </button>
      </li>
      <li v-if="props.showMore">
        <component :is="isDesktop ? 'tw-ui-dropdown' : 'tw-ui-modal'" class="action--more-actions">
          <template #title v-if="!isDesktop">
            {{ t('housing_card.more_actions') }}
          </template>
          <template #trigger="{ attrs, toggle }">
            <tw-ui-button
              v-bind="{ ...attrs, class: ['action--more-actions', 'tw-box-variant--circle', attrs.class], ariaLabel: t('show_more') }"
              @click="toggle()"
            >
              <Icon name="bi:three-dots" />
            </tw-ui-button>
          </template>

          <template #default="{ toggle }">
            <ul>
              <li>
                <tw-ui-button type="link" @click="checkIsLoggedIn('report', toggle)">{{ t('report_offer') }}</tw-ui-button>
              </li>
              <li>
                <tw-ui-button type="link" @click="checkIsLoggedIn('toggle_like', toggle, targetUserId, housingActionsWatched)">{{
                  t(`${!housingActionsWatched ? 'like' : 'unlike'}`)
                }}</tw-ui-button>
              </li>
              <li>
                <tw-ui-button type="link" @click="gotoHousing()">{{ t('goto') }}</tw-ui-button>
              </li>
              <li>
                <tw-housing-btn-share :housing="housing" type="link">
                  <template #button="{ toggle, attrs }">
                    <a href="#" v-bind="attrs" @click="toggle" class="share-btn">
                      {{ t('share') }}
                    </a>
                  </template>
                </tw-housing-btn-share>
              </li>
            </ul>
          </template>
        </component>
      </li>
    </ul>
    <tw-modal-conversion
      :more-actions-type="moreActionsType"
      :status="conversionModalStatus"
      @close-conversion-modal="handleNoteRegisteredInfo('close')"
      tracking-page="search"
      tracking-sub-category="buttons"
      tracking-area="modalRegistration"
    />
  </article>
</template>

<style lang="scss" scoped>
@import 'assets/styles/tw-variables';
@import 'assets/styles/tw-mixins';
@import 'assets/styles/tw-form';
@import 'assets/styles/tw-animations.scss';

:deep(.tw-icon) {
  width: 1em !important;
}

.tw-housing-content-actions-btn-like,
.tw-housing-content-actions-btn-unlike {
  display: inline-flex;
  border: none;
  background-color: rgba(33, 37, 41, 0.5);
  border-color: transparent;
  border-radius: 50%;
  width: fit-content;
  height: auto;
  aspect-ratio: 1 / 1;
  padding: 0.72rem;
  color: white;
  cursor: pointer;
  @include tw-is-desktop {
    background: transparent;
    padding: 0.5rem 0;
    color: $gray;
  }
  &:not(:disabled):focus,
  &:not(:disabled):hover,
  &:not(:disabled):active,
  &:not(:disabled):active {
    color: $gray;
    border: none;
    box-shadow: none;
    background-color: transparent;
  }
}

.tw-housing-content-actions-btn-unlike {
  color: $primary;
}

.heart-icon {
  width: 1.2rem;
  height: 1.2rem;
}

.heart-icon-loading {
  @include tw-pulse;
}

.tw-modal--content {
  p {
    margin: 0 0 1rem;
  }
}

.share-btn {
  display: inline-block;
  padding: 0.26rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.tw-housing-card {
  &.is--hidden,
  &.is--hidden.is--mobile {
    .tw-housing-card--card {
      flex-direction: row;
      flex-wrap: wrap;
      gap: calc(#{$tw-component--gap} * 4);

      .title {
        width: 100%;
        font-size: 1.25em;
      }

      .description {
        width: 100%;
        margin: 0;
        @include tw-colorset($gray);
      }

      .tw-housing-card--housing-info {
        padding: 0;

        :deep(.values) {
          font-weight: normal;
        }
      }

      .action--goto-housing {
        font-weight: bold;
        text-decoration-line: underline;
        @include tw-colorset($black, null, null, $black);
      }

      .action--reset {
        font-weight: bold;
        text-decoration-line: underline;
        @include tw-colorset($primary, null, null, $primary);

        &.is--hovered {
          @include tw-colorset($primary-variant, null, null, $primary-variant);
        }
      }
    }
  }

  &.is--mobile {
    .tw-button.action--like,
    .tw-button.action--more-actions {
      @include tw-colorset($white, rgba(33, 37, 41, 0.5), transparent);

      &.is--hovered,
      &.is--focused {
        @include tw-colorset($white, rgba(33, 37, 41, 0.4), transparent);
      }
    }
  }
}

.tw-housing-card {
  border: 1px solid $lightGray;
  box-shadow: 0 0.25em 0.5em rgb(0 0 0 / 25%);
  padding: 0;
  position: relative;

  /* disable hover styles for <a> */
  > a,
  > a:hover {
    color: inherit;
    text-decoration: none;
  }

  &.is--mobile {
    .tw-housing-card--card {
      flex-direction: column;

      .tw-housing-card--gallery {
        flex-basis: 100%;
      }

      .tw-housing-card--housing-info {
        flex-basis: 100%;
      }
    }

    .tw-housing-card--contact {
      border-top: 1px solid $lightGray;
      padding: 0.75em 1em;
      .tw-button {
        display: block;
        width: 100%;
      }
    }

    .tw-housing-card--gallery {
      :deep(.tw-carousel) {
        .tw-carousel--content {
          width: 100%;
          height: auto;
          aspect-ratio: 4/3;
        }
      }
    }

    .tw-housing-card--actions {
      top: 1.5em;
      right: 1.5em;

      .tw-button.action--like,
      .tw-button.action--more-actions {
        color: $white;
      }
    }
  }
}

.tw-housing-card--housing-info {
  padding: 0.75em 0;
  @include is-desktop {
    padding: 0.75em 0 0 1em;
  }
}
.tw-housing-card--search-info {
  padding: 0.75em 1em;
}

.tw-housing-card--card {
  display: flex;
  flex-flow: row nowrap;
  padding: 1em;

  .tw-housing-card--gallery,
  .tw-housing-card--contact {
    flex: 0 0 25%;
  }

  .tw-housing-card--housing-info {
    flex: 1 1 50%;
  }
}

.tw-housing-card--gallery {
  :deep(.tw-carousel) {
    .tw-carousel--content {
      height: 14em;
      aspect-ratio: 4/3;
    }

    .tw-carousel--controls-left,
    .tw-carousel--controls-right,
    .tw-carousel--navigation {
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
    }

    &.is--hovered {
      .tw-carousel--controls-left,
      .tw-carousel--controls-right,
      .tw-carousel--navigation {
        opacity: 1;
      }
    }
  }
}

.tw-housing-card--contact {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.tw-housing-card--search-info {
  border-top: 1px solid $lightGray;
}

.tw-housing-card--actions {
  position: absolute;
  top: calc(#{$tw-component--gap} * 2);
  right: calc(#{$tw-component--gap} * 2);
  left: unset;
  bottom: unset;
  display: flex;
  justify-content: flex-end;
  gap: calc(#{$tw-component--gap} * 2);
  list-style-type: none;
  padding: 0;
  z-index: 10;
}

.tw-button.action--like,
.tw-button.action--more-actions {
  padding: calc(#{$tw-component--gap} * 2);
  font-size: 1.2em;
  box-shadow: 0 0 0 transparent;
  transition:
    box-shadow 0.15s ease-in-out,
    color 0.15s ease-in-out,
    background-color 0.15s ease-in-out;
  @include tw-colorset($gray, transparent, transparent);

  &.is--hovered,
  &.is--focused {
    box-shadow: 0 0 0.3em rgba($white, 0.3);
    @include tw-colorset($gray, transparent, transparent);
  }
}

.tw-button.action--like {
  .tw-icon.is--filled {
    @include tw-colorset($primary);
  }

  &.is--loading {
    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(0.5);
      }
      100% {
        transform: scale(1);
      }
    }

    .tw-icon {
      animation-name: pulse;
      animation-duration: 1s;
      animation-timing-function: linear;
      animation-iteration-count: infinite;
    }
  }
}

.tw-button.action--contact {
  padding: calc(#{$tw-component--gap} * 2.5) calc(#{$tw-component--gap} * 8);
  font-weight: bold;
  text-decoration: underline;
  text-align: center;
  text-transform: uppercase;
  width: 100%;
  @include tw-colorset($white, $primary, $primary, transparent);
  @include tw-is-desktop {
    width: auto;
  }

  &:hover,
  &.is--hovered {
    @include tw-colorset($white, $primary-variant, $primary-variant, $white);
  }
}

.tw-dropdown.action--more-actions,
.tw-modal {
  ul {
    display: flex;
    flex-flow: column nowrap;
    gap: $tw-component--gap;
  }

  .tw-button.button-type--link {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    padding: $tw-component--gap;
  }
}
</style>

<i18n lang="json">
{
  "de": {
    "error": "Fehler",
    "img_alt": "Vorschaubild {0}",
    "contact_button": "Kontaktieren",
    "show_more": "Mehr anzeigen",
    "goto": "Angebot ansehen",
    "report_offer": "Angebot melden",
    "unlike": "Aus Merkliste entfernen",
    "like": "Zur Merkliste hinzufügen",
    "share": "Angebot teilen",
    "report": {
      "title": "Mehr Funktionen mit einem kostenlosen Konto!",
      "text": "Du möchtest eine Anzeige melden? Erstelle ein kostenloses Konto, um dies und viele weitere Funktionen freizuschalten.",
      "enumerations": [
        "Anzeigen melden, die nicht den Qualitätsstandards entsprechen.",
        "Angebote ausblenden, die für dich nicht relevant sind.",
        "Details zu interessanten Angeboten sofort einsehen.",
        "Angebote zur Merkliste hinzufügen und später vergleichen."
      ]
    },
    "toggle_like": {
      "title": "Noch kein Konto? Nutze die Merkliste!",
      "text": "Mit einem kostenlosen Konto kannst du deine Suche perfekt organisieren:",
      "enumerations": ["Merke interessante Angebote.", "Erhalte Benachrichtigungen bei neuen Angeboten.", "Nutze deine gespeicherte Suche."]
    },
    "contact": {
      "title": "Noch kein Konto? Kontaktiere deinen Tauschpartner direkt!",
      "text": "Erstelle ein kostenloses Konto, um den direkten Kontakt mit Tauschpartnern aufzunehmen und schnell Antworten zu erhalten:",
      "enumerations": [
        "Starte sofort den direkten Austausch mit potenziellen Tauschpartnern.",
        "Erhalte schnelle Antworten auf deine Anfragen.",
        "Verfolge deine Nachrichten und Anfragen an einem Ort."
      ]
    },
    "ad-not-added-to-watch-list": "Die Anzeige wurde nicht zu deiner Merkliste hinzugefügt.",
    "unexpected-error-occurred": "Ein unerwarteter Fehler ist aufgetreten."
  },
  "en": {
    "error": "Error",
    "img_alt": "Preview image {0}",
    "contact_button": "Contact",
    "show_more": "Show more",
    "goto": "View offer",
    "report_offer": "Report offer",
    "unlike": "Remove from watchlist",
    "like": "Add to watchlist",
    "share": "Share offer",
    "report": {
      "title": "More features with a free account!",
      "text": "You want to report an ad? Create a free account to unlock this and many other features.",
      "enumerations": [
        "Report ads that do not meet quality standards.",
        "Hide offers that are not relevant to you.",
        "View details of interesting offers immediately.",
        "Add offers to your watchlist and compare them later."
      ]
    },
    "toggle_like": {
      "title": "No account yet? Use the watchlist!",
      "text": "With a free account you can perfectly organize your search:",
      "enumerations": ["Mark interesting offers.", "Receive notifications of new offers.", "Use your saved search."]
    },
    "contact": {
      "title": "No account yet? Contact your exchange partner directly!",
      "text": "Create a free account to contact exchange partners directly and get quick answers:",
      "enumerations": [
        "Start the direct exchange with potential exchange partners immediately.",
        "Get quick answers to your inquiries.",
        "Track your messages and inquiries in one place."
      ]
    },
    "ad-not-added-to-watch-list": "The ad was not added to your watchlist.",
    "unexpected-error-occurred": "An unexpected error occurred."
  }
}
</i18n>
